import { SearchDropdown, SearchModal } from '@greystar/designsystem';
import {
  createInputChangeHandler,
  createSearchInputHandler,
  extractSearchBoxResults,
  handleSearchKeyDown,
  SearchBoxResult,
  setFocus,
  useControllerSubscription,
  useDropdownFocusHandler,
  useSearchAnalytics,
  useSearchResultClickHandler,
  handleSearchResultClick,
  createInputFocusHandler,
  handleDropdownFocusWithButton,
  handleInputClick,
  createInputModalFocusHandler,
  buildCommonInputProps,
  useTrackSearchModalOpen,
  createModalInputChangeHandler,
  getQuerySuggestString,
} from '@greystar/lib';
import { SearchBoxCsrProps, SearchModalProps } from '@greystar/ui';
import React, { useEffect, useState, JSX } from 'react';

type CommonSearchProps = {
  controllerSearchBox: SearchBoxCsrProps['controllerSearchBox'];
  controllerInstantProducts: SearchBoxCsrProps['controllerInstantProducts'];
  searchPromptText: string;
};

type EnhancedSearchBoxProps = CommonSearchProps & (SearchBoxCsrProps | SearchModalProps);

export const SearchBoxCsr = (props: EnhancedSearchBoxProps): JSX.Element => {
  // Coveo controllers
  const { controllerSearchBox, controllerInstantProducts } = props;

  // Coveo state management
  const [stateSearchBox, setStateSearchBox] = useState(controllerSearchBox.state);
  const [stateInstantProducts, setStateInstantProducts] = useState(controllerInstantProducts.state);

  // Custom state management
  const [items, setItems] = useState<SearchBoxResult[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [modalInputValue, setModalInputValue] = useState('');
  const [lastSuggestion, setLastSugestion] = useState('');
  const [inputIcon, setInputIcon] = useState<'Search' | 'CloseAlt' | undefined>('Search');

  // Hooks
  const { pushDataLayerEvent } = useSearchAnalytics();

  // Search result click handler
  useSearchResultClickHandler(searchInputValue.trim() === '' ? modalInputValue : searchInputValue);

  // Check click outside dropdown
  useDropdownFocusHandler(
    setIsInputFocused,
    setInputIcon,
    setSearchInputValue,
    setModalInputValue,
    setItems
  );

  useControllerSubscription(controllerSearchBox, setStateSearchBox);
  useControllerSubscription(controllerInstantProducts, setStateInstantProducts);

  const args = {
    isFocussed: isInputFocused,
    id: 'Search',
  };

  // Push dataLayer on opening search modal
  useTrackSearchModalOpen({
    variant: props.variant,
    trackEvent: pushDataLayerEvent,
  });

  // coveo related to update searchbox state
  useEffect(() => {
    const unsubscribe = controllerSearchBox.subscribe(() =>
      setStateSearchBox(controllerSearchBox.state)
    );
    return function cleanup() {
      unsubscribe();
    };
  }, [controllerSearchBox]);

  useEffect(() => {
    const unsubscribe = controllerInstantProducts.subscribe(() =>
      setStateInstantProducts(controllerInstantProducts.state)
    );
    return function cleanup() {
      unsubscribe();
    };
  }, [controllerInstantProducts]);

  useEffect(() => {
    setItems([]);

    if (modalInputValue.length > 2 || searchInputValue.length > 2) {
      setItems(extractSearchBoxResults(stateInstantProducts.products, stateInstantProducts.query));
    }
  }, [stateInstantProducts]);

  // Handle search button click on search input box
  const onDropdownFocus = () => {
    handleDropdownFocusWithButton({
      setIsInputFocused,
      setInputIcon,
      pushDataLayerEvent,
      setSearchInputValue,
      setItems,
      inputIcon,
      setModalInputValue,
    });
  };

  // Handle search input box changes and fetch result
  const onSearchBoxInputChange = createSearchInputHandler({
    controllerSearchBox,
    controllerInstantProducts,
    setItems,
    minQueryLength: 2,
  });

  // Handle Input box change to update search icon and get data from coveo
  const handleInputChange = createInputChangeHandler({
    setSearchInputValue,
    setInputIcon,
    onSearchBoxInputChange,
    setIsInputFocused,
  });

  // Handle Modal Input box change to update search icon and get data from coveo
  const handleModalInputChange = createModalInputChangeHandler({
    setModalInputValue,
    setInputIcon,
    onSearchBoxInputChange,
    setIsInputFocused,
  });

  // Handle Input box click / focus
  const handleInputFocus = createInputFocusHandler({
    setIsInputFocused,
    handleInputChange,
    setInputIcon,
  });

  // Handle Modal Input box click / focus
  const handleModalInputFocus = createInputModalFocusHandler({
    setIsInputFocused,
    setFocus,
    setInputIcon,
  });

  // Handle click on search result
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      handleSearchResultClick({
        event: e,
        searchInputValue,
        pushDataLayerEvent,
      });
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [pushDataLayerEvent, searchInputValue]);

  // Handle input box click event
  const onInputClick = () => {
    handleInputClick(pushDataLayerEvent);
  };

  // Handle search modal close event
  const handleModalClose = () => {
    pushDataLayerEvent('property_search_cancel', {
      actionType: 'cancel',
      search_term: searchInputValue,
    });
    if (props.variant === 'modal' && 'onClose' in props) {
      props.onClose?.();
    }
  };

  useEffect(() => {
    if (stateSearchBox.suggestions[0]) {
      const currentSuggestion = getQuerySuggestString(
        stateSearchBox.value,
        stateSearchBox.suggestions
      );
      if (lastSuggestion != currentSuggestion) {
        setItems([]);
        setLastSugestion(currentSuggestion);
      }
      controllerInstantProducts?.updateQuery(currentSuggestion);
    } else {
      controllerInstantProducts?.updateQuery(stateSearchBox.value);
    }
  }, [stateSearchBox.suggestions, controllerInstantProducts, stateSearchBox.value, lastSuggestion]);

  // Search input box events
  const commonInputProps = buildCommonInputProps({
    value: searchInputValue,
    onFocus: handleInputFocus,
    onChange: handleInputChange,
    onClick: onInputClick,
    onKeyDown: handleSearchKeyDown(items),
  });

  // Modal search input box events
  const commonModalProps = buildCommonInputProps({
    value: modalInputValue,
    onFocus: handleModalInputFocus,
    onChange: handleModalInputChange,
    onClick: onInputClick,
    onKeyDown: handleSearchKeyDown(items),
  });

  return props.variant === 'modal' ? (
    <SearchModal
      modalHeader={{
        headerTitle: props.searchPromptText,
        label: props.headerSubtitle,
        onClose: handleModalClose,
      }}
      searchInput={{
        placeholder: props.searchPromptText,
        inputId: 'modalSearch',
        inputProps: commonModalProps,
        iconName: inputIcon,
        buttonProps: { onClick: onDropdownFocus },
      }}
      searchResults={{
        title: props.searchResultsTitle,
        items,
        htmlFor: 'search',
      }}
    />
  ) : (
    <SearchDropdown {...args} size={props.Input.size}>
      <SearchDropdown.Input
        inputProps={{
          placeholder: isInputFocused ? '' : props?.Input?.inputProps.placeholder,
          ...commonInputProps,
        }}
        buttonProps={{ onClick: onDropdownFocus }}
        iconName={inputIcon}
      />
      <SearchDropdown.ModalTrigger
        label={props.ModalTrigger.label}
        onClick={props.ModalTrigger.onClick}
      />
      <SearchDropdown.Results
        labelProps={{
          label: props.Input.inputProps.placeholder,
        }}
        items={items}
      />
    </SearchDropdown>
  );
};
